import { useCallback, useContext, useEffect } from 'react';
import type { Channel } from 'pusher-js';
import { useQueryClient } from '@tanstack/react-query';

import pusherClient from '~/config/initPusher';
import log, { logError } from '~/common/utils/log';
import { UserContext } from '~/components/providers/UserProvider';
import { FEATURE } from '~/common/enums/feature.enum';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { usePusherEnabledForEndpoint } from '~/domains/pusher/hooks/usePusherEnabledForEndpoint';

import { MISSIONS_QUERY_KEY_PREFIX } from '../constants/query';
import {
  MISSION_PROGRESS_EVENT,
  MISSIONS_PUSHER_ENDPOINT,
  getMissionsChannelName,
} from '../constants/pusher';

/**
 * Real-time missions subscription (progressive enhancement over REST).
 *
 * Mounted once at the app root via the singleton `GamificationContextProvider`
 * (`MissionsRealtimeManager`), so this is a plain single-instance hook — no cross-mount
 * coordination.
 *
 * The `mission_progress` event is a lightweight "refetch now" signal: we never merge the
 * pushed payload into cache (so `rev` is unused and we always refetch). Also refetches on
 * (re)connect and on app foreground, because Pusher does not replay events missed while
 * disconnected. The shared `pusherClient` is never disconnected here — other features use it.
 */
export const useMissionsPusher = (): void => {
  const queryClient = useQueryClient();
  const isMissionsEnabled = useIsFeatureEnabled(FEATURE.ENABLE_MISSIONS);
  const pusherState = usePusherEnabledForEndpoint(MISSIONS_PUSHER_ENDPOINT);

  // Channel is keyed by the platform user id (`user.id`), NOT the legacy JWT `sub` claim
  // (see getAccessTokenData.ts).
  const { userId } = useContext(UserContext);

  const enabled = isMissionsEnabled && pusherState === 'on' && !!userId;

  const refetchMissions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [MISSIONS_QUERY_KEY_PREFIX] });
  }, [queryClient]);

  useEffect(() => {
    if (!enabled || !userId) {
      return undefined;
    }

    const channelName = getMissionsChannelName(userId);
    let channel: Channel | undefined;

    try {
      channel = pusherClient.subscribe(channelName);
      channel.bind(MISSION_PROGRESS_EVENT, refetchMissions);
      pusherClient.connection.bind('connected', refetchMissions);
      log('Subscribed to missions channel', { channelName });
    } catch (error) {
      logError('Failed to subscribe to missions channel', { error, channelName });
    }

    // App-foreground catch-up.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refetchMissions();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      channel?.unbind(MISSION_PROGRESS_EVENT, refetchMissions);
      pusherClient.connection.unbind('connected', refetchMissions);
      pusherClient.unsubscribe(channelName);
      log('Unsubscribed from missions channel', { channelName });
    };
  }, [enabled, userId, refetchMissions]);
};
